import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import jobsApi from '../../../APIs/jobs';
import { Button, Tag, Typography } from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import {
  InfoRowStyled,
  VerticalSpaceStyled,
} from '../../../style/reusableStyle';
import { humanReadableDateWithTime, requestAPI } from '../../../utils';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import { ReviewedByEscPlanContainerStyled } from './style';

type Props = {
  jobAndProposalId: string;
};

const ReviewedByEscPlan = ({ jobAndProposalId }: Props) => {
  const [loading, setLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);

  const jobProcess = useSelector((state: RootState) => state.singleJobProcess);
  const reviews = jobProcess?.review || [];
  const latestReview = reviews.reduce(
    (
      latest: (typeof jobProcess)['review'][0] | null,
      current: (typeof jobProcess)['review'][0],
    ) => {
      if (
        !latest ||
        new Date(current.reviewSendDate) > new Date(latest.reviewSendDate)
      ) {
        return current;
      }
      return latest;
    },
    null,
  );

  const onCloseModal = () => {
    setModalStatus(false);
  };
  const onSendToClient = () => {
    setModalStatus(true);
  };

  const handleSendToClient = async () => {
    try {
      setLoading(true);
      if (!latestReview) {
        setLoading(false);
        return;
      }
      const processId = jobProcess._id;
      const reviewId = latestReview._id;
      if (!processId || !reviewId) {
        setLoading(false);
        return;
      }
      await requestAPI(jobsApi.sendTotClient(processId, reviewId));
      toast.success('Sent successfully');
      await requestAPI(jobsApi.getJobProcessByJobProposal(jobAndProposalId));
      onCloseModal();
    } catch (error) {
      console.error('Error sending for review:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!latestReview) {
    return null;
  }
  const isReviewAccepted = latestReview?.reviewStatus === 'ACCEPT';
  const isSentToClient = latestReview
    ? jobProcess.isSendToClient?.includes(latestReview._id)
    : false;
  return (
    <ReviewedByEscPlanContainerStyled>
      <Typography strong fontColor="secondary" className="section-title">
        PDF REVIEW BY ESCAPE PLAN
      </Typography>
      <VerticalSpaceStyled $space={25}>
        <InfoRowStyled>
          <Typography className="title">Review Date :</Typography>
          <Typography className="value">
            {humanReadableDateWithTime(latestReview?.reviewDate)}
          </Typography>
        </InfoRowStyled>
        <InfoRowStyled $direction="column">
          <Typography className="title">Notes from Escape Plan :</Typography>
          <div className="value">
            <div>
              {latestReview?.reviews?.map((review) => (
                <Typography
                  key={review.substring(0, 50)}
                  style={{ marginBottom: '8px' }}
                >
                  {review}
                </Typography>
              ))}
            </div>
          </div>
        </InfoRowStyled>
        {!isReviewAccepted ? (
          <InfoRowStyled>
            <Typography className="title">Status</Typography>
            <div className="value">
              <Tag type="danger">Review Rejected by Escape Plan</Tag>
            </div>
          </InfoRowStyled>
        ) : (
          <InfoRowStyled>
            <Typography className="title">Status</Typography>
            <div className="value">
              <Tag type="success">Review Accepted by Escape Plan </Tag>
            </div>
          </InfoRowStyled>
        )}

        {!isSentToClient ? (
          <Button variant="secondary" width="141px" onClick={onSendToClient}>
            Sent to Client
          </Button>
        ) : (
          <div>
            <Typography variant="title" fontColor="secondary">
              All Reviewed documents (accepted) are now visible to client.
            </Typography>
            <Typography variant="caption" fontColor="secondary">
              Now client will review, and respond to you soon.
            </Typography>
          </div>
        )}
      </VerticalSpaceStyled>

      <ConfirmationModal
        isOpen={modalStatus}
        onClose={onCloseModal}
        message="Are you sure you want to sent to client?"
        title="Send to client"
        onOk={handleSendToClient}
        okText="Send"
        loading={loading}
      />
    </ReviewedByEscPlanContainerStyled>
  );
};

export default ReviewedByEscPlan;
